package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.contests.entity.PerfectLineupEntity
import com.fantasytenis.shared.domain.contests.entity.PerfectLineupSlotEntity
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.lineup.entity.LineupEntity
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.ThemeColors
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Perfect Lineup Section - displays comparison between user's picks and the optimal lineup.
 *
 * This component shows users how their lineup compared to the mathematically optimal
 * lineup after a contest completes. It's designed to be educational and encouraging,
 * framing differences as learning opportunities rather than failures.
 */
@Composable
fun PerfectLineupSection(
    perfectLineup: PerfectLineupEntity,
    userLineup: LineupEntity?,
    playersMap: Map<String, TennisPlayerEntity>,
    colors: ThemeColors
) {
    var isExpanded by remember {
        mutableStateOf(
            try {
                localStorage.getItem("perfectLineupExpanded") != "false"
            } catch (e: Exception) {
                true
            }
        )
    }

    // Calculate comparison metrics
    val userTotal = userLineup?.totalPoints ?: 0.0
    val perfectTotal = perfectLineup.totalPoints
    val percentOfPerfect = if (perfectTotal > 0) (userTotal / perfectTotal * 100).coerceIn(0.0, 100.0) else 0.0
    val matchedPicks = countMatchedPicks(userLineup, perfectLineup)

    // Save expanded state
    LaunchedEffect(isExpanded) {
        try {
            localStorage.setItem("perfectLineupExpanded", isExpanded.toString())
        } catch (e: Exception) {
            // Ignore storage errors
        }
    }

    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            marginBottom(24.px)
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header (clickable)
        Div({
            style {
                padding(16.px, 20.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                cursor("pointer")
                backgroundColor(if (isExpanded) colors.surfaceVariant else colors.surface)
                property("transition", "background-color 0.2s")
            }
            onClick { isExpanded = !isExpanded }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                // Trophy icon
                Span({
                    style {
                        fontSize(24.px)
                    }
                }) {
                    Text("\uD83C\uDFC6") // Trophy emoji
                }

                Div {
                    H3({
                        style {
                            fontSize(16.px)
                            fontWeight(600)
                            color(colors.textPrimary)
                            margin(0.px)
                        }
                    }) {
                        Text("PERFECT LINEUP")
                    }
                    Span({
                        style {
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("See what the optimal picks would have been")
                    }
                }
            }

            // Expand/collapse icon
            Span({
                style {
                    fontSize(20.px)
                    color(colors.textSecondary)
                    property("transition", "transform 0.2s")
                    if (isExpanded) {
                        property("transform", "rotate(180deg)")
                    }
                }
            }) {
                Text("\u25BC") // Down arrow
            }
        }

        // Summary bar (always visible)
        Div({
            style {
                padding(12.px, 20.px)
                backgroundColor(colors.highlightBackground)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                flexWrap(FlexWrap.Wrap)
                gap(12.px)
            }
        }) {
            // Percentage score
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                if (userLineup != null) {
                    Span({
                        style {
                            fontSize(24.px)
                            fontWeight(700)
                            color(if (percentOfPerfect >= 90) colors.success else colors.textPrimary)
                        }
                    }) {
                        Text("${percentOfPerfect.toInt()}%")
                    }
                    Span({
                        style {
                            fontSize(14.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("of perfect")
                    }
                } else {
                    Span({
                        style {
                            fontSize(14.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("No entry submitted")
                    }
                }
            }

            // Matched picks count
            if (userLineup != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                    }
                }) {
                    when (matchedPicks) {
                        5 -> {
                            Span({
                                style {
                                    fontSize(14.px)
                                    fontWeight(600)
                                    color(Colors.Medal.GoldBackground)
                                }
                            }) {
                                Text("PERFECT!")
                            }
                        }
                        else -> {
                            Span({
                                style {
                                    fontSize(14.px)
                                    fontWeight(500)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("$matchedPicks of 5 optimal picks")
                            }
                        }
                    }
                }
            }

            // Total points comparison
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                    fontSize(13.px)
                }
            }) {
                if (userLineup != null) {
                    Span({
                        style {
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("You: ")
                        Span({
                            style {
                                fontWeight(600)
                                color(colors.textPrimary)
                            }
                        }) {
                            Text("${userTotal.toInt()} pts")
                        }
                    }
                }
                Span({
                    style {
                        color(colors.textSecondary)
                    }
                }) {
                    Text("Perfect: ")
                    Span({
                        style {
                            fontWeight(600)
                            color(colors.primary)
                        }
                    }) {
                        Text("${perfectTotal.toInt()} pts")
                    }
                }
            }
        }

        // Expandable content - category-by-category breakdown
        if (isExpanded) {
            Div({
                style {
                    padding(20.px)
                }
            }) {
                // Category comparison rows
                Category.entries.forEach { category ->
                    val perfectSlot = perfectLineup.getSlotForCategory(category)
                    val userPlayerId = userLineup?.slots?.getPlayerForCategory(category)
                    val userPoints = userLineup?.categoryScores?.getScoreForCategory(category)
                    val userPlayer = userPlayerId?.let { playersMap[it] }

                    CategoryComparisonRow(
                        category = category,
                        userPlayerId = userPlayerId,
                        userPlayerName = userPlayer?.name,
                        userPoints = userPoints,
                        perfectSlot = perfectSlot,
                        colors = colors
                    )
                }

                // Cost comparison
                Div({
                    style {
                        marginTop(16.px)
                        paddingTop(16.px)
                        property("border-top", "1px solid ${colors.border}")
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Span({
                        style {
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("Salary used:")
                    }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(16.px)
                        }
                    }) {
                        if (userLineup != null) {
                            Span({
                                style {
                                    fontSize(13.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("You: ")
                                Span({
                                    style {
                                        fontWeight(600)
                                        color(colors.textPrimary)
                                    }
                                }) {
                                    Text("$${userLineup.totalSalaryCost}K")
                                }
                            }
                        }
                        Span({
                            style {
                                fontSize(13.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("Perfect: ")
                            Span({
                                style {
                                    fontWeight(600)
                                    color(colors.primary)
                                }
                            }) {
                                Text("$${perfectLineup.totalCost}K")
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Single category comparison row showing user's pick vs optimal pick.
 */
@Composable
private fun CategoryComparisonRow(
    category: Category,
    userPlayerId: String?,
    userPlayerName: String?,
    userPoints: Double?,
    perfectSlot: PerfectLineupSlotEntity,
    colors: ThemeColors
) {
    val isMatch = userPlayerId == perfectSlot.playerId
    val pointsDiff = (userPoints ?: 0.0) - perfectSlot.points
    val categoryColor = getCategoryColorValue(category)

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(12.px, 0.px)
            property("border-bottom", "1px solid ${colors.borderLight}")
        }
    }) {
        // Category indicator and name
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                width(100.px)
                flexShrink(0)
            }
        }) {
            // Category color bar
            Div({
                style {
                    width(4.px)
                    height(32.px)
                    backgroundColor(categoryColor)
                    borderRadius(2.px)
                }
            })
            Div {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(colors.textSecondary)
                        property("text-transform", "uppercase")
                        letterSpacing(0.5.px)
                        marginLeft(4.px)
                    }
                }) {
                    Text(category.displayName.take(3))
                }
            }
        }

        // User's pick (or empty state)
        Div({
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
                padding(0.px, 12.px)
                if (isMatch) {
                    backgroundColor(colors.successLight)
                    borderRadius(6.px)
                    padding(8.px, 12.px)
                }
            }
        }) {
            if (userPlayerId != null) {
                Span({
                    style {
                        fontSize(13.px)
                        fontWeight(500)
                        color(colors.textPrimary)
                    }
                }) {
                    Text(userPlayerName ?: "Player")
                }
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("${(userPoints ?: 0.0).toInt()} pts")
                }
            } else {
                Span({
                    style {
                        fontSize(13.px)
                        color(colors.textDisabled)
                        fontStyle("italic")
                    }
                }) {
                    Text("Not selected")
                }
            }
        }

        // VS indicator or match checkmark
        Div({
            style {
                width(40.px)
                textAlign("center")
                flexShrink(0)
            }
        }) {
            if (isMatch) {
                Span({
                    style {
                        fontSize(18.px)
                        color(colors.success)
                    }
                }) {
                    Text("\u2713") // Checkmark
                }
            } else if (userPlayerId != null) {
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(600)
                        color(colors.error)
                    }
                }) {
                    Text(if (pointsDiff >= 0) "+${pointsDiff.toInt()}" else "${pointsDiff.toInt()}")
                }
            }
        }

        // Perfect pick
        Div({
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
                padding(0.px, 12.px)
                backgroundColor(colors.surfaceVariant)
                borderRadius(6.px)
                padding(8.px, 12.px)
            }
        }) {
            Span({
                style {
                    fontSize(13.px)
                    fontWeight(500)
                    color(colors.textPrimary)
                }
            }) {
                Text(perfectSlot.playerName)
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(600)
                        color(colors.primary)
                    }
                }) {
                    Text("${perfectSlot.points.toInt()} pts")
                }
                Span({
                    style {
                        fontSize(11.px)
                        color(colors.textSecondary)
                        backgroundColor(categoryColor)
                        padding(2.px, 6.px)
                        borderRadius(4.px)
                        color(Colors.White)
                    }
                }) {
                    Text("$${perfectSlot.cost}K")
                }
            }
        }
    }
}

/**
 * Count how many of the user's picks matched the perfect lineup.
 */
private fun countMatchedPicks(userLineup: LineupEntity?, perfectLineup: PerfectLineupEntity): Int {
    if (userLineup == null) return 0

    var count = 0
    Category.entries.forEach { category ->
        val userPlayerId = userLineup.slots.getPlayerForCategory(category)
        val perfectPlayerId = perfectLineup.getSlotForCategory(category).playerId
        if (userPlayerId == perfectPlayerId) {
            count++
        }
    }
    return count
}

/**
 * Get the color for a category.
 */
private fun getCategoryColorValue(category: Category): CSSColorValue {
    return when (category) {
        Category.SERVE -> Colors.Category.Serve
        Category.FOREHAND -> Colors.Category.Forehand
        Category.BACKHAND -> Colors.Category.Backhand
        Category.VOLLEYS -> Colors.Category.Volleys
        Category.FIGHTING -> Colors.Category.Fighting
    }
}
